import React, { useCallback, useMemo, useState } from "react";

import {
  faCircleInfo,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { Column, createColumnHelper } from "@tanstack/react-table";

import { useAllOrganizationUsers } from "src/api/taktile/queries";
import { Card } from "src/base-components/Card";
import { ColumnMultiValueFilter } from "src/base-components/ColumnFilter";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Pill } from "src/base-components/Pill";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import {
  OrganizationUser,
  SimpleRoleType,
  WorkspaceRole,
} from "src/clients/taktile-api";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { User } from "src/flow/User";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "src/router/featureFlags";
import { DashboardPageParams } from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { CreateInviteModal } from "src/userManagement/CreateInviteModal/CreateInviteModal";
import { Status } from "src/userManagement/Status";
import {
  CellRendererProps,
  UserRow,
  UserStatus,
} from "src/userManagement/types";
import { UserActions } from "src/userManagement/UserActions";
import { DUMMY_PERMISSION_SLOTS } from "src/userManagement/userPermissions/permissionFeatures";
import {
  RolePillDropdown,
  RolePillOption,
} from "src/userManagement/userPermissions/RolePillDropdown";
import {
  WORKSPACE_ROLES_NAMES,
  WorkspaceRolesRenderer,
} from "src/userManagement/WorkspaceRolesRenderer";
import { errorMessage } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

const roleLabels: Record<SimpleRoleType, string> = {
  ADMIN: "Admin",
  MEMBER: "Team Member",
};

const USER_NAME_IS_NOT_DEFINED = "No username";

const NO_WORKSPACE_ROLE = "none";

const STATUS_FILTER_OPTIONS: { key: UserStatus; value: string }[] = [
  { key: "active", value: "Active" },
  { key: "invited", value: "Invited" },
  { key: "deactivated", value: "Deactivated" },
];

const DEFAULT_STATUS_FILTER: UserStatus[] = ["active", "invited"];

const sameStatuses = (a: UserStatus[], b: UserStatus[]) =>
  a.length === b.length && a.every((status) => b.includes(status));

const cellRenderer = (
  info: CellRendererProps<string | undefined>,
  dataLoc?: string,
) => {
  return (
    <Cell dataLoc={dataLoc} info={info}>
      <span className="block max-w-90 truncate">{info.getValue()}</span>
    </Cell>
  );
};

const UserRenderer = (info: CellRendererProps<string>) => {
  const userId = useCurrentUserId();
  const value = info.getValue() || USER_NAME_IS_NOT_DEFINED;
  const user = info.row.original;
  return (
    <Cell dataLoc="user-name" info={info}>
      <span className="block max-w-90 truncate">
        <User
          avatar={user.avatar_url}
          name={userId === user.id ? `${value} (You)` : value}
        />
      </span>
    </Cell>
  );
};

const PermissionsRenderer = (info: CellRendererProps<SimpleRoleType>) => {
  const user = info.row.original;

  if (user.status === "deactivated") {
    return <Cell info={info} />;
  }

  return (
    <Cell info={info}>
      <Pill dataLoc={`org-role-${user.username}`} size="sm" variant="gray">
        <Pill.Text>{roleLabels[info.getValue()]}</Pill.Text>
      </Pill>
    </Cell>
  );
};

const WORKSPACE_ROLE_OPTIONS: RolePillOption[] = [
  { key: NO_WORKSPACE_ROLE, label: "None" },
  ...Object.entries(WORKSPACE_ROLES_NAMES).map(([key, label]) => ({
    key,
    label,
  })),
];

const WorkspaceRoleRenderer = (info: CellRendererProps<unknown>) => {
  const user = info.row.original;

  if (user.status === "deactivated") {
    return <Cell info={info}>No Access</Cell>;
  }

  return (
    <Cell info={info}>
      <RolePillDropdown
        dataLoc={`workspace-role-${user.username}`}
        options={WORKSPACE_ROLE_OPTIONS}
        selectedKey={user.workspaceRoleKey ?? NO_WORKSPACE_ROLE}
        onSelect={(roleKey) => user.onWorkspaceRoleChange?.(roleKey)}
      />
    </Cell>
  );
};

const PermissionQuotaRenderer = (info: CellRendererProps<unknown>) => {
  const user = info.row.original;
  const used = user.status === "deactivated" ? 0 : DUMMY_PERMISSION_SLOTS.used;

  return (
    <Cell dataLoc={`permission-quota-${user.username}`} info={info}>
      {used > 0 ? `${used}/${DUMMY_PERMISSION_SLOTS.total} keys` : "-"}
    </Cell>
  );
};

const columnHelper = createColumnHelper<UserRow>();

const BaseColumns = [
  columnHelper.accessor("id", {
    header: (info) => (
      <Header info={info}>
        <span className="pl-1">ID</span>
        <Tooltip placement="top" title="User ID">
          <div className="ml-1">
            <Icon icon={faCircleInfo} size="2xs" />
          </div>
        </Tooltip>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <CopyTextIcon
          dataLoc="user-id"
          feedback="inline"
          tooltip="Copy user ID"
          value={info.getValue()}
        />
      </Cell>
    ),
    minSize: 55,
    maxSize: 55,
    meta: {
      sticky: "left",
    },
  }),
  columnHelper.accessor((row) => row.full_name || row.username, {
    id: "full_name_or_username",
    header: (info) => <Header info={info}>User</Header>,
    cell: UserRenderer,
    minSize: 200,
    maxSize: 200,
    filterFn: "includesString",
  }),
  columnHelper.accessor("email", {
    header: (info) => <Header info={info}>Email</Header>,
    cell: (info) => cellRenderer(info),
    maxSize: 200,
    minSize: 200,
    filterFn: "includesString",
  }),
];

const statusCell = (info: CellRendererProps<UserStatus>) => (
  <Cell info={info}>
    <Status
      dataLoc={`org-user-status-${info.row.original.username}`}
      status={info.row.original.status}
    />
  </Cell>
);

const StatusColumn = columnHelper.accessor("status", {
  header: (info) => <Header info={info}>Status</Header>,
  cell: statusCell,
  minSize: 100,
  maxSize: 100,
});

const StatusFilterControl: React.FC<{
  column: Column<UserRow, UserStatus>;
}> = ({ column }) => {
  const applied = (column.getFilterValue() as UserStatus[]) ?? [];
  const [draft, setDraft] = useState<UserStatus[]>(applied);

  return (
    <ColumnMultiValueFilter
      dataLoc="filter-users-status"
      defaultValue={DEFAULT_STATUS_FILTER}
      headline="Filter by status:"
      isApplied={sameStatuses(draft, applied)}
      isFiltering={applied.length > 0}
      options={STATUS_FILTER_OPTIONS}
      values={draft}
      onApply={(statuses) =>
        column.setFilterValue(statuses.length > 0 ? statuses : undefined)
      }
      onChange={setDraft}
      onResetRequest={() => {
        setDraft([]);
        column.setFilterValue(undefined);
      }}
    />
  );
};

// Kept referentially stable: `flexRender` renders the header as a component
// type, so rebuilding this def would remount the dropdown mid-selection
const FilterableStatusColumn = columnHelper.accessor("status", {
  header: (info) => (
    <Header info={info}>
      <span className="inline-flex items-center gap-1">
        Status
        <StatusFilterControl column={info.column} />
      </span>
    </Header>
  ),
  cell: statusCell,
  filterFn: (row, _columnId, filterValue: UserStatus[]) =>
    filterValue.length === 0 || filterValue.includes(row.original.status),
  minSize: 167,
  maxSize: 167,
});

const OrganizationRoleColumn = columnHelper.accessor("role", {
  header: (info) => <Header info={info}>Organization role</Header>,
  cell: (info) => (
    <PermissionsRenderer {...(info as CellRendererProps<SimpleRoleType>)} />
  ),
  minSize: 160,
  maxSize: 160,
});

const WorkspacePermissionsColumn = columnHelper.accessor("workspaces_roles", {
  header: (info) => <Header info={info}>Workspace permissions</Header>,
  cell: (info) => (
    <Cell info={info}>
      <WorkspaceRolesRenderer
        {...(info as unknown as CellRendererProps<WorkspaceRole[]>)}
      />
    </Cell>
  ),
});

const WorkspaceRoleColumn = columnHelper.display({
  id: "workspace_role",
  header: (info) => <Header info={info}>Workspace Role</Header>,
  cell: WorkspaceRoleRenderer,
  minSize: 236,
  maxSize: 236,
});

const PermissionQuotaColumn = columnHelper.display({
  id: "permission_quota",
  header: (info) => <Header info={info}>Permission quota</Header>,
  cell: PermissionQuotaRenderer,
  minSize: 213,
  maxSize: 213,
});

const ActionsColumn = columnHelper.display({
  id: "actions",
  header: (info) => <Header info={info} />,
  cell: (info) => (
    <Cell dataLoc="user-actions" info={info}>
      {["invited", "active", "deactivated"].includes(
        info.row.original.status,
      ) && <UserActions user={info.row.original} />}
    </Cell>
  ),
  minSize: 60,
  maxSize: 60,
  meta: {
    sticky: "right",
  },
});

const LegacyColumns = [
  ...BaseColumns,
  StatusColumn,
  OrganizationRoleColumn,
  WorkspacePermissionsColumn,
  ActionsColumn,
];

const GranularColumns = [
  ...BaseColumns,
  FilterableStatusColumn,
  WorkspaceRoleColumn,
  PermissionQuotaColumn,
  ActionsColumn,
];

const UsersTable: React.FC<{
  users: OrganizationUser[];
  isLoading: boolean;
  searchQuery?: string;
}> = ({ users, isLoading, searchQuery }) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const userId = useCurrentUserId();
  const granularPermissionsEnabled = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.granularPermissions,
  );
  const [editUser, setEditUser] = useState<Nullable<OrganizationUser>>(null);
  // Held locally until DEC-9983 wires the workspace role to the backend
  const [workspaceRoles, setWorkspaceRoles] = useState<Record<string, string>>(
    {},
  );

  const workspaceRoleProps = useCallback(
    (user: OrganizationUser) => ({
      workspaceRoleKey:
        workspaceRoles[user.id] ??
        user.workspaces_roles?.find(
          (workspaceRole) => workspaceRole.workspace_id === wsId,
        )?.role ??
        NO_WORKSPACE_ROLE,
      onWorkspaceRoleChange: (roleKey: string) =>
        setWorkspaceRoles((previous) => ({ ...previous, [user.id]: roleKey })),
    }),
    [workspaceRoles, wsId],
  );

  const rows = useMemo(() => {
    const inviteRows = users
      .filter((u) => !u.authenticated)
      .map(
        (invite) =>
          ({
            ...invite,
            status: "invited",
            onEdit: () => setEditUser(invite),
            ...workspaceRoleProps(invite),
          }) as UserRow,
      );

    const activeRows = users
      .filter((u) => u.is_active && u.authenticated)
      .map(
        (user) =>
          ({
            ...user,
            status: "active",
            onEdit: () => setEditUser(user),
            ...workspaceRoleProps(user),
          }) as UserRow,
      );

    const inactiveRows = users
      .filter((u) => !u.is_active)
      .map(
        (user) =>
          ({
            ...user,
            status: "deactivated",
          }) as UserRow,
      );

    return [...activeRows, ...inviteRows, ...inactiveRows];
  }, [users, workspaceRoleProps]);

  return (
    <>
      <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
        <Card.Content className="min-h-0 flex-1">
          <Table
            columns={
              granularPermissionsEnabled ? GranularColumns : LegacyColumns
            }
            data={rows}
            getRowId={(row) => row.id}
            globalFilter={searchQuery}
            initialState={
              granularPermissionsEnabled
                ? {
                    columnFilters: [
                      { id: "status", value: DEFAULT_STATUS_FILTER },
                    ],
                  }
                : undefined
            }
            isLoading={isLoading}
            rowClassName="hover:bg-gray-50"
            clientSideFiltering
          />
        </Card.Content>
      </Card>
      <CreateInviteModal
        editableOrgRole={editUser?.id !== userId}
        open={Boolean(editUser)}
        organizationId={orgId}
        user={editUser}
        onClose={() => setEditUser(null)}
      />
    </>
  );
};

export const UserList: React.FC<{ searchQuery?: string }> = ({
  searchQuery,
}) => {
  const { orgId } = useParamsDecode<DashboardPageParams>();
  const {
    isLoading,
    data: usersResult,
    isError,
    error,
  } = useAllOrganizationUsers(orgId, true);

  const users = usersResult?.pages.flat() ?? [];

  if (isError) {
    return (
      <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
        <Card.Content className="min-h-0 flex-1">
          <EmptyState
            dataLoc="users-error-state"
            description={errorMessage(error)}
            headline="Error loading users"
            icon={faTriangleExclamation}
            variant="error"
          />
        </Card.Content>
      </Card>
    );
  }

  return (
    <UsersTable isLoading={isLoading} searchQuery={searchQuery} users={users} />
  );
};
